/** Embeddable widget runtime contract (docs/plan/screens-auth-app-widget.md §4, mockups/widget). */

/** `<script data-*>` attributes read by the loader (`sagot-widget.js` port). */
export const WIDGET_DATA_ATTRIBUTES = {
  agent: "data-agent",
  color: "data-color",
  position: "data-position",
  title: "data-title",
  subtitle: "data-subtitle",
  mode: "data-mode",
  target: "data-target",
  user: "data-user",
  identityToken: "data-identity-token",
  lead: "data-lead",
  theme: "data-theme",
  radius: "data-radius",
  label: "data-label",
  greeting: "data-greeting",
  /** Optional API origin override (defaults to the build's API origin, `https://api.<root>`). */
  api: "data-api",
} as const;

export const WIDGET_HOST_TAG = "sagot-widget";
export const WIDGET_DEFAULT_COLOR = "#00aeef";
export const WIDGET_DEFAULT_RADIUS_PX = 16;
export const WIDGET_RADIUS_RANGE = { min: 0, max: 24 } as const;
export const WIDGET_Z_INDEX = 2147483000;
/** Below this viewport width the panel is full-screen (radius 0, launcher hidden while open). */
export const WIDGET_MOBILE_MAX_PX = 479;

export const WIDGET_TIMINGS_MS = {
  greetingDelay: 2500,
  attachNote: 6000,
  copied: 1400,
  reconnectedBanner: 3000,
  focusDelay: 60,
  rateLimitDefault: 60_000,
} as const;

export const WIDGET_RUNTIME_LIMITS = {
  messageChars: 1000,
  greetingChars: 120,
  starters: 4,
  feedbackNoteChars: 500,
  offlineMessageChars: 500,
} as const;

/** First-party storage keys on the host page (prefix `sagot:`; never cookies). */
export const WIDGET_STORAGE_KEYS = {
  greetingDismissed: (agent: string) => `sagot:greeting-dismissed:${agent}`,
  visitor: (agent: string) => `sagot:visitor:${agent}`,
  conversation: (agent: string) => `sagot:conversation:${agent}`,
  lead: (agent: string) => `sagot:lead:${agent}`,
  /** Set in session storage once a page-visit trigger fired for this tab session (the server owns the frequency rules). */
  pageTrigger: (agent: string, triggerId: string) => `sagot:page-trigger:${agent}:${triggerId}`,
  /** Set in session storage when a click workflow was reported: when (every click, 30 s apart) or that it was (once, every session). */
  clickTrigger: (agent: string, triggerId: string) => `sagot:click-trigger:${agent}:${triggerId}`,
} as const;

/** `Sagot('identify', { getToken })`: when the loader fetches a fresh identity token. */
export const WIDGET_IDENTITY_TIMINGS_MS = {
  /** Refresh this long before the token's `exp`. */
  refreshEarly: 60_000,
  /** Never refresh sooner than this after the last fetch (very short or unreadable `exp`). */
  minDelay: 5_000,
  /** Token without a readable `exp`. */
  fallbackRefresh: 8 * 60_000,
  /** The page's token endpoint failed: first retry after this, doubling each time… */
  retry: 30_000,
  /** …up to this gap… */
  retryMax: 10 * 60_000,
} as const;

/** …and after this many failures in a row the loader stops asking until the next `identify` or 401. */
export const WIDGET_IDENTITY_MAX_FAILURES = 5;

/** How long a visitor token is remembered (widget Behavior `remember_days` default). */
export const WIDGET_VISITOR_TTL_DAYS = 7;

/**
 * Host-page limits of `Sagot('identify', { name, email, attributes })` and `Sagot('track', name, props)`.
 * They are the server's own rules (backend `schemas/public_visitors.py`, `schemas/common.py` and
 * `core/constants/workflow_time.py`), so nothing the server accepts is refused on the page and nothing it would
 * refuse is sent: the loader drops invalid entries with a console warning, and the server validates again.
 */
export const WIDGET_VISITOR_LIMITS = {
  nameChars: 120,
  emailChars: 254,
  /** The server's e-mail rule: a local part of 1-64 characters, a dotted domain whose last label has 2 or more. */
  emailPattern: /^[^@\s]{1,64}@[^@\s]+\.[^@\s]{2,}$/,
  /** Flat `attributes` of identify: at most 20 string / number / boolean values, or a list of strings (a `list` attribute). */
  attributeKeys: 20,
  attributeKeyPattern: /^[a-z][a-z0-9_]{0,39}$/,
  attributeValueChars: 500,
  /** A list value: at most 20 choices of 80 characters each. */
  attributeListItems: 20,
  attributeListItemChars: 80,
  /** An event name is any text of 1 to 60 characters once trimmed (workflows match it with a condition on `event.name`). */
  eventNameChars: 60,
  /** Flat `props` of track. */
  eventPropKeys: 20,
  eventPropKeyPattern: /^[A-Za-z0-9_.-]{1,40}$/,
  eventPropValueChars: 200,
  /** Good-citizen throttle: `track` calls beyond this per sliding minute are dropped on the page. */
  eventsPerMinute: 30,
  /** Calls waiting for the widget to become ready / for the previous call to finish. */
  queuedCalls: 100,
} as const;

/** `visitor_visits_page`: the loader evaluates the public config's `page_triggers` on the host page. */
export const WIDGET_PAGE_TRIGGER = {
  /** Value of `trigger` in `POST /public/v1/workflow-runs`. */
  trigger: "visitor_visits_page",
  /** `caps` of that request: this client renders workflow cards (same token as `@sagot/chat` `WORKFLOW_CAP`). */
  caps: ["workflow_steps"],
  urlOps: ["is", "contains", "starts_with"],
  maxTriggers: 20,
  maxRules: 10,
  ruleValueChars: 500,
  idChars: 64,
  maxSeconds: 3600,
  /** A page timer may fire a hair early relative to `Date.now()`: counts as due. */
  toleranceMs: 50,
  /** The backend `PageIn` limit of `page.url` / `page.referrer`. */
  urlChars: 2048,
} as const;

/** `visitor_clicks_element`: the loader watches clicks on the host page for the public config's `click_triggers`. */
export const WIDGET_CLICK_TRIGGER = {
  /** Value of `trigger` in `POST /public/v1/workflow-runs`. */
  trigger: "visitor_clicks_element",
  caps: ["workflow_steps"],
  frequencies: ["every_click", "once", "every_session"],
  maxTriggers: 20,
  maxSelectors: 5,
  idChars: 64,
  /** The same workflow is reported at most once per this long (the server debounces too). */
  debounceMs: 30_000,
} as const;

export const WIDGET_EVENTS = ["ready", "open", "close", "message", "handoff", "lead"] as const;
export type WidgetEventName = (typeof WIDGET_EVENTS)[number];

export const WIDGET_COMMANDS = [
  "open",
  "close",
  "toggle",
  "identify",
  "logout",
  "setColor",
  "setLocale",
  "newConversation",
  "setPosition",
  "setTheme",
  "setLead",
  "showGreeting",
  "ask",
  "reset",
  "track",
] as const;
export type WidgetCommand = (typeof WIDGET_COMMANDS)[number];

export const WIDGET_LOCALES = ["en", "fil"] as const;
export type WidgetLocale = (typeof WIDGET_LOCALES)[number];

/**
 * Optional contact phone on lead forms (DEC-041): international E.164, any country —
 * `+`, country code, 8–15 digits in total. Spaces, dashes, dots and brackets are ignored.
 */
export const PHONE_E164_PATTERN = /^\+[1-9]\d{7,14}$/;
export const PHONE_MAX_CHARS = 24;
export const PHONE_PLACEHOLDER = "+1 415 555 0123";

export function normalizePhone(value: string): string {
  return value.replace(/[\s().-]/g, "");
}

/** Empty is valid (phone is optional); otherwise E.164 after normalizing. */
export function isValidPhone(value: string): boolean {
  const phone = normalizePhone(value);
  return phone.length === 0 || PHONE_E164_PATTERN.test(phone);
}
